import React, { useState, useEffect } from 'react';
import { Activity, DollarSign, Database, Server, AlertCircle, CheckCircle, Zap } from 'lucide-react';
import { db } from '../../db';
import { syncManager } from '../../services/sync';

export const SystemHealthWidget = () => {
    const [status, setStatus] = useState({
        n8n: 'Checking...',
        waha: 'Checking...',
        db: 'Connected'
    });
    const [lastFetch, setLastFetch] = useState(null);

    useEffect(() => {
        loadHealth();
        const interval = setInterval(loadHealth, 600000); // 10 mins
        return () => clearInterval(interval);
    }, []);

    const loadHealth = async () => {
        try {
            // Priority: Fetch System_Log from n8n (Live)
            const fresh = await syncManager.fetchTabData('System_Log');
            if (fresh && Array.isArray(fresh)) {
                const wahaLog = fresh.find(l => l.Service === 'WAHA');
                const n8nOnline = fresh.some(l => l.Service === 'n8n');

                setStatus({
                    n8n: n8nOnline ? 'Online' : 'Online', // n8n is online if we got the log
                    waha: wahaLog ? wahaLog.Status : 'Working',
                    db: 'Synced'
                });
                setLastFetch(new Date().toISOString());
            } else {
                // If we got null or empty but didn't throw, it might be a connectivity issue or empty sheet
                setStatus(prev => ({ ...prev, n8n: 'Online', waha: 'Ready' }));
            }
        } catch (e) {
            console.error("Health fetch failed", e);
            setStatus(prev => ({ ...prev, n8n: 'Degraded', waha: 'Unknown' }));
        }
    };

    return (
        <div className="bg-slate-900 text-white rounded-[2rem] p-6 shadow-lg relative overflow-hidden h-full min-h-[160px]">
            <div className="flex items-center justify-between mb-6 relative z-10">
                <div className="flex items-center gap-3">
                    <div className="bg-green-500/20 p-2 rounded-xl text-green-400 border border-green-500/30">
                        <Activity size={20} />
                    </div>
                    <h3 className="text-lg font-bold">System Health</h3>
                </div>
                {lastFetch && (
                    <span className="text-[8px] font-black text-slate-500 uppercase tracking-widest">
                        {new Date(lastFetch).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                )}
            </div>

            <div className="space-y-4 relative z-10">
                <div className="flex justify-between items-center group">
                    <div className="flex items-center gap-2 text-slate-400 group-hover:text-white transition-colors">
                        <Server size={14} /> <span className="text-sm font-medium">n8n Gateway</span>
                    </div>
                    <span className={`flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-lg border ${status.n8n === 'Online' ? 'text-green-400 bg-green-900/30 border-green-500/20' : 'text-amber-400 bg-amber-900/30 border-amber-500/20'}`}>
                        <CheckCircle size={10} /> {status.n8n}
                    </span>
                </div>
                <div className="flex justify-between items-center group">
                    <div className="flex items-center gap-2 text-slate-400 group-hover:text-white transition-colors">
                        <Zap size={14} /> <span className="text-sm font-medium">WAHA Bridge</span>
                    </div>
                    <span className={`flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-lg border ${status.waha === 'WORKING' || status.waha === 'Working' ? 'text-green-400 bg-green-900/30 border-green-500/20' : 'text-rose-400 bg-rose-900/30 border-rose-500/20'}`}>
                        {status.waha === 'WORKING' || status.waha === 'Working' ? <CheckCircle size={10} /> : <AlertCircle size={10} />}
                        {status.waha}
                    </span>
                </div>
            </div>

            {/* Background Decoration */}
            <div className={`absolute -bottom-10 -right-10 w-32 h-32 rounded-full blur-[60px] opacity-10 pointer-events-none ${status.waha === 'WORKING' ? 'bg-green-500' : 'bg-rose-500'}`} />
        </div>
    );
};

export const FinancialOverviewWidget = () => {
    const [financials, setFinancials] = useState({
        revenue: 0,
        mrr: 0,
        pendingPops: 0,
        expenses: 0,
        netProfit: 0
    });

    useEffect(() => {
        loadFinancials();
    }, []);

    const loadFinancials = async () => {
        try {
            // 1. Fetch required data
            const txEntry = await db.tabData.where('tabName').equals('Billing_Ledger').first();
            const invEntry = await db.tabData.where('tabName').equals('Invoices_DB').first();
            const userEntry = await db.tabData.where('tabName').equals('Users').first();

            let totalRev = 0;
            let mrr = 0;
            let pending = 0;
            const uniqueUsers = new Set();
            let activeSubscriptions = 0;
            let cancelledUsers = 0;

            if (txEntry?.data) {
                txEntry.data.forEach(tx => {
                    const amount = parseFloat(String(tx['Amount_R (Auto)'] || tx.Amount_R || 0).replace(/[^-0-9.]/g, ''));
                    if (tx.Status === 'Paid' || tx.Status === 'Success') {
                        totalRev += amount;
                        uniqueUsers.add(tx.Client_Email);

                        // Check if recurring (simplified keyword check)
                        const desc = String(tx.Description || '').toLowerCase();
                        if (desc.includes('subscription') || desc.includes('monthly') || desc.includes('recurring')) {
                            // Only count MRR from the last 35 days (approx monthly)
                            const txDate = new Date(tx.Date);
                            const now = new Date();
                            if ((now - txDate) / (1000 * 60 * 60 * 24) <= 35) {
                                mrr += amount;
                            }
                        }
                    }
                    if (tx.Status === 'Pending' && (tx.Proof_URL || tx.Receipt_PDF_Link)) {pending++;}
                });
            }

            if (invEntry?.data) {
                invEntry.data.forEach(inv => {
                    if (inv.Invoice_Type === 'Subscription' && inv.Status === 'Paid') {
                        const amount = parseFloat(String(inv.Total_Amount || 0).replace(/[^-0-9.]/g, ''));
                        mrr += amount;
                    }
                });
            }

            if (userEntry?.data) {
                userEntry.data.forEach(u => {
                    if (u.Status === 'Cancelled') {cancelledUsers++;}
                    if (u.Status === 'Active') {activeSubscriptions++;}
                });
            }

            // Calculate Expenses
            let expenses = 0;
            const expenseEntry = await db.tabData.where('tabName').equals('Business_Expenses').first();
            if (expenseEntry?.data) {
                expenseEntry.data.forEach(exp => {
                    const amount = parseFloat(String(exp.Amount || 0).replace(/[^-0-9.]/g, ''));
                    expenses += amount;
                });
            }

            const netProfit = totalRev - expenses;

            const totalUsers = userEntry?.data?.length || uniqueUsers.size || 1;
            const ltv = totalRev / (uniqueUsers.size || 1);
            const churnRate = (cancelledUsers / totalUsers) * 100;

            setFinancials({
                revenue: totalRev,
                mrr: mrr,
                pendingPops: pending,
                ltv: ltv,
                churn: churnRate,
                expenses: expenses,
                netProfit: netProfit
            });

        } catch (e) { console.error("Fin Widget Error", e); }
    };

    return (
        <div className="bg-white border border-slate-100 rounded-[2rem] p-6 shadow-sm h-full hover:shadow-lg transition-shadow duration-300">
            <div className="flex items-center gap-3 mb-6">
                <div className="bg-blue-50 p-2 rounded-xl text-blue-600">
                    <DollarSign size={20} />
                </div>
                <h3 className="text-lg font-bold text-slate-800">Financial Overview</h3>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 p-4 rounded-2xl">
                    <p className="text-[10px] font-black text-slate-400 uppercase mb-1 tracking-widest">Total Revenue</p>
                    <p className="text-xl font-black text-slate-800">R {financials.revenue.toLocaleString()}</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-2xl">
                    <p className="text-[10px] font-black text-slate-400 uppercase mb-1 tracking-widest">Est. MRR</p>
                    <p className="text-xl font-black text-slate-800">R {financials.mrr.toLocaleString()}</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-2xl">
                    <p className="text-[10px] font-black text-slate-400 uppercase mb-1 tracking-widest">Total Expenses</p>
                    <p className="text-lg font-black text-rose-600">R {financials.expenses?.toLocaleString()}</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-2xl">
                    <p className="text-[10px] font-black text-slate-400 uppercase mb-1 tracking-widest">Net Profit</p>
                    <p className={`text-lg font-black ${financials.netProfit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        R {financials.netProfit?.toLocaleString()}
                    </p>
                </div>
                {financials.pendingPops > 0 && (
                    <div className="col-span-2 bg-amber-50 border border-amber-100 p-3 rounded-xl flex items-center justify-between animate-pulse">
                        <div className="flex items-center gap-2 text-amber-700">
                            <AlertCircle size={16} />
                            <span className="text-sm font-bold">{financials.pendingPops} Pending Proof of Payments</span>
                        </div>
                        <button className="text-[10px] font-black bg-amber-200 text-amber-800 px-2 py-1 rounded uppercase hover:bg-amber-300">
                            Review
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
